import { formatDate, PurchaseItemStatus } from "common";
import { memoize } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import { DigitalWaiver, ExperiencePurchaseItem, Timeslot } from "types";
import shallow from "zustand/shallow";
import { getTimeslotParams } from "../Common/timeslotParams";
import { getAttendeesUniqueness } from "../helper";
import { getColumnDefs } from "../Roster/RosterGuestList/helpers";
import { getExperiencesForTimeslots } from "./gridUtils";
import {
    combineTimeslotsWithPurchaseItems,
    combineTimeslotsWithPurchaseItemsAndEvents,
    getRowData,
    groupTimeslots,
    sortGroupedData,
} from "./tableHelpers";
import { useDashboardTableStore } from "./useDashboardTableStore";
import { useQuestionnairePreference } from "@/hooks/questionnaireFormHooks";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCaptureQueryError } from "@/hooks/useCaptureQueryError";
import { useCollection } from "@/hooks/useCollection";
import { useDashboardFilterStore, useModalStore } from "@/stores/dashboardFilterStore";
import { canViewWaivers } from "@/stores/globalStore";
import { DashboardGroupBy } from "@/types/Dashboard";

const includedStatuses = new Set([
    PurchaseItemStatus.CONFIRMED,
    PurchaseItemStatus.PENDING_ACCEPT,
    PurchaseItemStatus.PENDING_MINIMUM,
]);

export interface GroupedData {
    groupLabel: string;
    items: ExperiencePurchaseItem[];
    allAttendeesUnique: boolean;
    someAttendeesUnique: boolean;
    rowData: any[];
}

const useCombinedData = (timeslots: Timeslot[], filteredItems: ExperiencePurchaseItem[]) => {
    const groupBy = useDashboardFilterStore.getState().filters.groupBy;
    const combined = useMemo(
        () =>
            [DashboardGroupBy.EQUIPMENT, DashboardGroupBy.GUIDE, DashboardGroupBy.EVENT].includes(groupBy)
                ? combineTimeslotsWithPurchaseItemsAndEvents(timeslots, filteredItems)
                : combineTimeslotsWithPurchaseItems(timeslots, filteredItems),
        [timeslots, filteredItems, groupBy],
    );

    return combined;
};

export const useDashboardTableData = () => {
    const [filters, showSummaryRows] = useDashboardFilterStore(
        (state) => [state.filters, state.displayOptions.showSummaryRows],
        shallow,
    );

    const groupBy = filters.groupBy;
    const { data: timeslots, isFetching: isTimeslotsLoading } = useTimeslots();
    const { data: purchaseItems, isLoading, isFetching } = usePurchaseItems();
    const isPurchaseItemsLoading = isLoading || isFetching;

    const isModalOpen = useModalStore.getState().isOpen;

    const groupHeaderData = useDashboardTableStore((state) => state.groupHeaderData);
    const { viewConfigurations } = useQuestionnairePreference([]);
    const selectedView = useDashboardTableStore.getState().selectedTableView;

    const config = useMemo(() => {
        return {
            viewConfigurations,
            selectedView,
            isTimeslotsLoading,
        };
    }, [viewConfigurations, selectedView, isTimeslotsLoading]);

    const experiences = useMemo(() => getExperiencesForTimeslots(timeslots), [timeslots]);

    useCollection<DigitalWaiver>("waiverTemplates", null, {
        enabled: canViewWaivers(),
    });

    const tableColumnDefs = memoizedTableColumnDefs(config, experiences);

    const filteredItemsRef = useRef<ExperiencePurchaseItem[]>();

    useEffect(() => {
        useDashboardTableStore.setState({ isLoading: isPurchaseItemsLoading });
    }, [isPurchaseItemsLoading]);

    const filteredItems = useMemo(() => {
        const items = purchaseItems.filter((item) => includedStatuses.has(item.status));
        if (isModalOpen) {
            return filteredItemsRef.current ?? items;
        }
        filteredItemsRef.current = items;
        return items;
    }, [purchaseItems, isModalOpen]);

    const combinedData = useCombinedData(timeslots, filteredItems);

    const { groupTables, groupLabels } = useMemo(
        () => groupTimeslots(combinedData, groupBy as DashboardGroupBy),
        [combinedData, groupBy],
    );

    const groupedDataUnsorted = useMemo(() => {
        return groupTables.map((group) => {
            const purchaseItems = group.items.map((item) => item.purchaseItem);
            const rowDataWithSummary = getRowData(purchaseItems, filters);
            const rowData = showSummaryRows ? rowDataWithSummary : rowDataWithSummary.slice(1);
            const groupExperiences = experiences.filter((experience) =>
                group.items.some((item) => item.timeslot.product === experience.id),
            );

            const { allAttendeesUnique, someAttendeesUnique } = getAttendeesUniqueness(groupExperiences);

            return {
                groupLabel: group.groupLabel,
                items: purchaseItems,
                allAttendeesUnique,
                someAttendeesUnique,
                rowData: rowData,
            };
        });
    }, [groupTables, showSummaryRows, experiences, filters]);

    const groupedDataRef = useRef(groupedDataUnsorted);
    const rawGroupedData = useMemo(
        () => sortGroupedData(groupedDataUnsorted, groupHeaderData),
        [groupedDataUnsorted, groupHeaderData],
    );

    const groupedData = useMemo(() => {
        if (isModalOpen) return groupedDataRef.current ?? rawGroupedData;
        groupedDataRef.current = rawGroupedData;
        return rawGroupedData;
    }, [rawGroupedData, isModalOpen]);

    // Save table headers data. Stores the data needed for rendering the table headers.
    useEffect(() => {
        if (groupLabels.length === 0) return;

        const { groupHeaderData } = useDashboardTableStore.getState();
        for (const label of groupLabels) {
            if (groupHeaderData[label]) continue;
            useDashboardTableStore.getState().actions.loadGroupHeader(groupBy, label);
        }
    }, [groupLabels, groupBy]);

    const hasNoData = purchaseItems.length === 0 || timeslots.length === 0;

    return {
        timeslots,
        experiences,
        groupedData,
        tableColumnDefs: tableColumnDefs.data,
        isPurchaseItemsLoading,
        hasNoData,
    };
};

export const useTimeslots = () => {
    const currentDate = useDashboardFilterStore((state) => state.date);
    const filters = useDashboardFilterStore.getState().filters;

    const params = useMemo(() => {
        const getParams = getTimeslotParams();
        return getParams(currentDate, null, { products: filters.products.selected });
    }, [currentDate, filters]);

    const {
        data: timeslots,
        isFetching,
        error: timeslotError,
    } = useAutoFetchCollection<Timeslot>("timeslots", params, {
        enabled: currentDate !== null,
        keepPreviousData: true,
        staleTime: 5000,
        notifyOnChangeProps: ["data", "error", "isFetching", "isFetchingNextPage", "isLoading"],
    });

    useCaptureQueryError(timeslotError, {
        tags: { component: "DashboardList", action: "fetchTimeslots" },
        extra: { start: currentDate, params },
    });

    return useMemo(
        () => ({
            data: timeslots,
            currentDate: formatDate(currentDate),
            isFetching: isFetching,
        }),
        [timeslots, currentDate, isFetching],
    );
};

const expandParams = "waiver,membershipPurchaseItem,purchaseRuleLink,purchaseRule";

const usePurchaseItems = () => {
    const currentDate = useDashboardFilterStore((state) => state.date);

    const params = useMemo(
        () => ({
            arrivalDate: formatDate(currentDate),
            expand: expandParams,
        }),
        [currentDate],
    );

    const {
        data: purchaseItems,
        isLoading,
        isFetching,
    } = useAutoFetchCollection<ExperiencePurchaseItem>("purchaseItems", params, {
        enabled: !!currentDate,
        staleTime: 60 * 1000,
        keepPreviousData: true,
    });

    return useMemo(
        () => ({
            data: purchaseItems,
            isLoading,
            isFetching,
        }),
        [purchaseItems, isLoading, isFetching],
    );
};

// Sending params as object here is important, memoize checks for the first argument being different to invalidate the cache
const memoizedTableColumnDefs = memoize((configs: any, experiences: any) => {
    return getColumnDefs(configs.viewConfigurations, undefined, experiences);
});
